import { EyeOutlined } from '@ant-design/icons';
import {
  Button,
  Form,
  Grid,
  Image,
  Input,
  message,
  Spin,
  Upload,
  UploadFile,
  UploadProps,
} from 'antd';
import { Modal } from 'components';
import { useCreateDashBoard } from 'features/Dashboard/use-cases/upload-documents';
import { useUploadImage } from 'features/Dashboard/use-cases/upload-image';
import { useUserContext } from 'features/users';
import { StyledUserModalWrapper } from 'features/users/components/modals/user-modal.styles';
import { useState } from 'react';
import { convertFileSize, validateFileBeforeUpload } from 'routes/utils';

const { useBreakpoint } = Grid;

export const UploadDocumentsLegalItemModal = ({
  onClose,
  refetch,
}: {
  onClose: () => void;
  refetch: () => void;
}) => {
  const formName = 'add-category-form';
  const screens = useBreakpoint();
  const [validateTrigger, setValidateTrigger] = useState(['onSubmit']);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [previewImage, setPreviewImage] = useState<string>('');
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [form] = Form.useForm();
  const { user } = useUserContext();
  const { mutate, isLoading } = useCreateDashBoard();
  const [filenameUrl, setFileNameUrl] = useState('');
  const [isImageUploading, setIsImageUploading] = useState<boolean>(false); // Loading state for image upload
  const { isLoading: imageLoading, mutate: uploadImage } = useUploadImage();
  const [blobUrl, setBlobUrl] = useState<string>(''); // Store the uploaded blob URL

  const handleImageUpload = (file: UploadFile) => {
    const formData = new FormData();
    formData.append('file', file as any);
    setIsImageUploading(true); // Set uploading state to true
    uploadImage(
      { id: user?.CompanyId as string, formData }, // Pass id and file as payload
      {
        onSuccess: (response: any) => {
          setFileNameUrl(response.fileName);
          setBlobUrl(response?.blobUrl);
          message.success('File uploaded successfully');
          setIsImageUploading(false); // Reset uploading state
        },
        onError: () => {
          message.error('Image upload failed');
          setIsImageUploading(false); // Reset uploading state
        },
      },
    );
  };
  const handleUpdate = (formValue: any) => {
    if (!fileList.length) {
      form.setFields([
        { name: 'document', errors: ['Please upload a document'] },
      ]);

      return;
    }
    const formData = new FormData();
    formData.append('CompanyId', user?.CompanyId);
    formData.append('documentName', formValue?.documentName);
    formData.append('DocumentTypeId', 6 as any);
    const uploadedFile = fileList[0]?.originFileObj;
    if (uploadedFile) {
      // Get the filename and filesize from the file object
      const fileSize = uploadedFile.size;
      // Append filename and filesize to FormData
      formData.append('FileName', filenameUrl);
      formData.append('FileSize', convertFileSize(fileSize)); // FileSize is typically a number, so convert it to string
    }
    mutate(formData, {
      onSuccess: () => {
        refetch();
        onClose();
      },
      onError: (error) => {
        console.error('Upload failed:', error);
      },
    });
  };
  const beforeUpload = (file: any) => {
    const isValid = validateFileBeforeUpload(file);
    if (isValid) {
      handleImageUpload(file); // Call handleImageUpload only if valid
    }

    return isValid; // If false, upload will be prevented
  };
  const handlePreview = async (file: UploadFile) => {
    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
  };
  const handleRemoveFile = () => {
    setFileList([]);
    setBlobUrl(''); // Reset the blob URL when the file is removed
  };
  const handleChange: UploadProps['onChange'] = ({ fileList: newFileList }) =>
    setFileList(newFileList);

  return (
    <StyledUserModalWrapper>
      <Modal
        open
        centered
        title='Legal Documents'
        onCancel={onClose}
        getContainer={false}
        onOk={form.submit}
        okText='Upload'
        cancelButtonProps={{ style: { display: 'none' } }}
        width={screens.lg ? '520px' : '100%'}
        okButtonProps={{
          form: formName,
          loading: isLoading,
          disabled: imageLoading || isImageUploading,
        }}
        maskClosable={false}
      >
        <Form
          id={formName}
          name={formName}
          form={form}
          layout='vertical'
          onFinish={handleUpdate}
          onFinishFailed={() => {
            setValidateTrigger(['onChange']);
          }}
          validateTrigger={validateTrigger}
          requiredMark={true}
        >
          <Form.Item
            name='documentName'
            label='Document Name'
            rules={[
              {
                required: true,
                message: 'Please enter the document name',
              },
            ]}
          >
            <Input placeholder='Enter document name' />
          </Form.Item>

          <Form.Item
            name='document'
            valuePropName='fileList'
            rules={[
              {
                required: true,
                message: 'Please upload a document',
              },
            ]}
          >
            <div className='upld_item'>
              <div className='updcbx'>
                <Upload
                  action='https://660d2bd96ddfa2943b33731c.mockapi.io/api/upload'
                  listType='picture-card'
                  fileList={fileList}
                  onPreview={handlePreview}
                  onChange={handleChange}
                  maxCount={1}
                  disabled={isLoading}
                  beforeUpload={beforeUpload}
                  onRemove={handleRemoveFile}
                  className='upld_docs'
                >
                  {fileList?.length >= 1 ? null : (
                    <div className='upld_img'>
                      <img src='/upload-img.png' alt='Upload' />
                      <p className='drp_text'>
                        Drag and drop here <span>or</span>{' '}
                        <Button type='link'>Browse Files</Button>
                      </p>
                    </div>
                  )}
                </Upload>
                {blobUrl && fileList.length > 0 && (
                  <EyeOutlined
                    className='eyeiconi'
                    onClick={() => setPreviewOpen(true)}
                  />
                )}
              </div>
              <Modal
                open={previewOpen}
                onCancel={() => setPreviewOpen(false)}
                footer={null}
                centered
                width={800}
              >
                {blobUrl.endsWith('.jpg') ||
                blobUrl.endsWith('.jpeg') ||
                blobUrl.endsWith('.png') ||
                blobUrl.endsWith('.webp') ? (
                  <Image src={blobUrl} alt='Preview' />
                ) : blobUrl.endsWith('.pdf') ? (
                  <iframe
                    src={`https://docs.google.com/gview?url=${blobUrl}&embedded=true`}
                    style={{ width: '100%', height: '600px', border: 'none' }}
                    title='PDF Preview'
                  />
                ) : (
                  <iframe
                    src={`https://docs.google.com/gview?url=${blobUrl}&embedded=true`}
                    style={{ width: '100%', height: '600px', border: 'none' }}
                    title='Document Preview'
                  />
                )}
              </Modal>

              {isImageUploading && (
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 1,
                  }}
                >
                  <Spin size='large' />
                </div>
              )}
            </div>
          </Form.Item>

          {previewImage && (
            <Image
              wrapperStyle={{ display: 'none' }}
              preview={{
                visible: previewOpen,
                onVisibleChange: (visible) => setPreviewOpen(visible),
                afterOpenChange: (visible) => !visible && setPreviewImage(''),
              }}
              src={previewImage}
            />
          )}
        </Form>
      </Modal>
    </StyledUserModalWrapper>
  );
};
